import {
  Box,
  Button,
  Code,
  InlineStack,
  Stack,
  Text,
  useModals,
  useSnackbars,
} from '@customerio/pluma-components/react';
import { usePermissions } from '@customerio/hydra-permissions/react';
import { ViewerRestrictedTooltip } from '@/components/overlays/viewer-restricted-tooltip';
import { generatePath } from '@/services/router/generate-path';
import { isEmptyString } from '@/utils/string';
import { isRecord } from '@/utils/objects';
import { segmentPath } from './cells';
import { FormattedLogSource } from './formatted-log-source';
import { LogJsonFormatter, type GeoRefResolver, type SegmentLinkResolver } from './log-json-formatter';
import { ResendEventModal } from './resend-event-modal';
import { activityLogJson } from '../log-json';
import { useGeoRefResolver } from '../use-geo-refs';
import type { IdentifierConfig } from '../display-name';
import type { ActivityLog, ActivityLogRow, ActivityLogSegment } from '../types';

/**
 * Expanded-row detail: the event data (via `LogJsonFormatter`, with identifier/reserved labels,
 * humanized timestamps, segment links, and channel-formatted reasons), the source description(s),
 * the anonymous id, Merge-profiles, Resend, and Copy JSON.
 *
 * Deferred: the inbound-message preview + its JSON toggle.
 */

/** Hide action sources for newsletter/transactional (broken data). */
function filteredSources(log: ActivityLog) {
  return (log.sources ?? []).filter((source) => {
    if (source.type !== 'action') return true;
    return !log.name.includes('newsletter') && !log.name.includes('transactional');
  });
}

/** The merged anonymous id, if any. */
function mergedAnonymousId(log: ActivityLog): string | undefined {
  if (log.type === 'anon_merge') return undefined;
  const source = (log.sources ?? []).find((s) => !isEmptyString(s.anonymous_id ?? ''));
  return source?.anonymous_id ?? undefined;
}

/** The `conflicting_people_id` from one identifier-keyed attr value, if it carries one. */
function conflictingIdFrom(value: unknown): string | null {
  if (!isRecord(value)) return null;
  const id = value.conflicting_people_id;
  return typeof id === 'string' || typeof id === 'number' ? String(id) : null;
}

/**
 * For a failed attribute change, the first `conflicting_people_id` across the identifier-keyed
 * attrs — the profile the Merge action would resolve against.
 */
function conflictingCustomerId(log: ActivityLog): string | null {
  if (log.type !== 'failed_attribute_change') return null;
  if (!isRecord(log.attrs)) return null;
  for (const value of Object.values(log.attrs)) {
    const id = conflictingIdFrom(value);
    if (id != null) return id;
  }
  return null;
}

/** Merge-profiles link, pre-filling the conflicting ids — or undefined when Merge doesn't apply. */
function mergeHrefFor(log: ActivityLog, workspaceId: string, conflictId: string | null): string | undefined {
  if (conflictId == null || isEmptyString(log.customer_id ?? '')) return undefined;
  return generatePath(
    '/workspaces/:workspaceId/journeys/people/merge',
    { workspaceId },
    { primary_id: log.customer_id ?? undefined, secondary_id: conflictId },
  );
}

/** All the payload-derived, hook-free state the expanded row needs (kept pure for readability). */
function deriveExpandedRowState(row: ActivityLogRow, isAnonymous: boolean, workspaceId: string) {
  const { log, customer } = row;
  const copyable = activityLogJson(log);
  const cioId = customer?.internal_id ?? '';
  const mergeHref = mergeHrefFor(log, workspaceId, conflictingCustomerId(log));
  return {
    sources: filteredSources(log),
    anonId: isAnonymous ? undefined : mergedAnonymousId(log),
    // Displayed event data is the raw `attrs`.
    jsonString: log.attrs == null ? null : JSON.stringify(log.attrs, null, 2),
    // Copy JSON is gated on the normalized json value (undefined for many types → no button),
    // and copies that value, not the raw attrs.
    copyable,
    showCopyJson: copyable !== undefined,
    cioId,
    mergeHref,
    showMerge: mergeHref != null,
    // Resend: an Event log for a known profile can be re-sent.
    showResend: !isAnonymous && log.type === 'event' && !isEmptyString(log.customer_id ?? '') && !isEmptyString(cioId),
  };
}

type EventDataBlockProps = {
  log: ActivityLog;
  jsonString: string | null;
  identifiers: IdentifierConfig;
  resolveSegmentLink: SegmentLinkResolver;
  resolveGeoRef: GeoRefResolver | undefined;
};

function EventDataBlock({ log, jsonString, identifiers, resolveSegmentLink, resolveGeoRef }: EventDataBlockProps) {
  if (jsonString == null) {
    return (
      <Text size="s" color="subtle">
        No event data
      </Text>
    );
  }
  return (
    // `fs-mask`: event attributes can contain customer PII — redact the whole block from FullStory
    // session replay (masking the full event data is the safe superset).
    <Box className="fs-mask">
      <LogJsonFormatter
        data={log.attrs}
        logType={log.type}
        identifiers={identifiers}
        showNestedIdentifiers={log.type === 'profile_merge'}
        resolveSegmentLink={resolveSegmentLink}
        resolveGeoRef={resolveGeoRef}
        formatErrorType={log.delivery_type}
      />
    </Box>
  );
}

type SourcesBlockProps = {
  sources: ReturnType<typeof filteredSources>;
  log: ActivityLog;
  workspaceId: string;
  campaignSingular: string;
};

function SourcesBlock({ sources, log, workspaceId, campaignSingular }: SourcesBlockProps) {
  if (sources.length === 0) return null;
  return (
    <Box px="200" pb="100">
      <Text size="s" color="subtle" weight="bold" as="span">
        {sources.length === 1 ? 'Source: ' : 'Sources:'}
      </Text>
      {sources.length === 1 ? (
        <Text size="s" color="subtle" as="span">
          <FormattedLogSource
            log={log}
            source={sources[0]}
            workspaceId={workspaceId}
            campaignSingular={campaignSingular}
          />
        </Text>
      ) : (
        <Stack gap="050">
          {sources.map((source, index) => (
            <Text key={index} size="s" color="subtle" as="span">
              <FormattedLogSource
                log={log}
                source={source}
                workspaceId={workspaceId}
                campaignSingular={campaignSingular}
              />
            </Text>
          ))}
        </Stack>
      )}
    </Box>
  );
}

type AnonymousIdBlockProps = { anonId: string | undefined };

function AnonymousIdBlock({ anonId }: AnonymousIdBlockProps) {
  if (anonId == null) return null;
  return (
    <Box pl="200" pb="100">
      <Text size="s" color="subtle" weight="bold" as="span">
        anonymous_id
      </Text>
      <Text size="s" color="subtle" as="span">
        {': '}
        {/* The anonymous id is an end-user pseudonymous identifier — keep it out of FullStory. */}
        <Code size="xxs" className="fs-mask">
          {anonId}
        </Code>
      </Text>
    </Box>
  );
}

type RowActionsProps = {
  showMerge: boolean;
  mergeHref: string | undefined;
  showResend: boolean;
  /** Whether the user may actually resend (journeys/test_events/send). */
  canResend: boolean;
  onResend: () => void;
  showCopyJson: boolean;
  onCopyJson: () => void;
};

function RowActions({
  showMerge,
  mergeHref,
  showResend,
  canResend,
  onResend,
  showCopyJson,
  onCopyJson,
}: RowActionsProps) {
  if (!showMerge && !showResend && !showCopyJson) return null;
  return (
    <InlineStack pr="200" pt="150" flexShrink="0" gap="200" alignInline="flex-end">
      {showMerge && mergeHref != null ? (
        <Button variant="secondary" size="sm" href={mergeHref}>
          Merge profiles…
        </Button>
      ) : null}
      {showResend ? (
        // Show Resend for any eligible log, but disable (with the role-restriction tooltip) when the
        // user lacks send permission — rather than opening a dead read-only modal.
        <ViewerRestrictedTooltip readOnly={!canResend}>
          {(context, effectiveReadOnly) => (
            <Button
              ref={context.reference.setReference}
              {...context.reference.getReferenceProps()}
              variant="secondary"
              size="sm"
              isDisabled={effectiveReadOnly}
              onClick={onResend}
            >
              Resend
            </Button>
          )}
        </ViewerRestrictedTooltip>
      ) : null}
      {showCopyJson ? (
        <Button variant="secondary" size="sm" onClick={onCopyJson}>
          Copy JSON
        </Button>
      ) : null}
    </InlineStack>
  );
}

type ExpandedRowProps = {
  row: ActivityLogRow;
  workspaceId: string;
  campaignSingular: string;
  isAnonymous: boolean;
  /** Workspace identifier config, for the JSON formatter's "Identifier" badges. */
  identifiers: IdentifierConfig;
  /** Sideloaded segments (by id), for resolving `entered`/`exited` arrays to name links. */
  segmentById: Map<number, ActivityLogSegment>;
  /** Refetch the log list after a successful resend. */
  onResent: () => void;
};

export function ExpandedRow({
  row,
  workspaceId,
  campaignSingular,
  isAnonymous,
  identifiers,
  segmentById,
  onResent,
}: ExpandedRowProps) {
  const { log, customer } = row;
  const snackbars = useSnackbars();
  const modals = useModals();
  const { hasPermission } = usePermissions();
  const canResend = hasPermission('journeys/test_events/send');

  const { sources, anonId, jsonString, copyable, showCopyJson, cioId, mergeHref, showMerge, showResend } =
    deriveExpandedRowState(row, isAnonymous, workspaceId);

  // Resolve the geoset/geofence id attrs on a geofence transition event to name links; only
  // fetches for geofence logs, once the row is expanded.
  const resolveGeoRef = useGeoRefResolver(log, workspaceId);

  // Resolve segment ids in `entered`/`exited` arrays to name links from the sideloaded segments
  //; a missing id renders as "Deleted segment (id)".
  const resolveSegmentLink: SegmentLinkResolver = (id) => {
    const segment = segmentById.get(id);
    return segment == null ? null : { name: segment.name, href: segmentPath(workspaceId, segment.id) };
  };

  const openResend = () => {
    modals.open(ResendEventModal, {
      environmentId: workspaceId,
      cioId,
      recipientLabel: customer?.identifiers?.email ?? log.customer_id ?? cioId,
      eventName: log.name,
      eventData: jsonString ?? '{}',
      onSent: onResent,
    });
  };

  const copyJson = () => {
    if (!showCopyJson) return;
    navigator?.clipboard
      ?.writeText(JSON.stringify(copyable, null, 2))
      .then(() => snackbars.create({ message: 'Copied JSON!' }))
      .catch(() => snackbars.create({ message: 'Failed to copy JSON!', variant: 'error' }));
  };

  return (
    <InlineStack alignBlock="flex-start" alignInline="space-between" gap="200">
      <Box flexGrow="1">
        <Box p="200">
          <EventDataBlock
            log={log}
            jsonString={jsonString}
            identifiers={identifiers}
            resolveSegmentLink={resolveSegmentLink}
            resolveGeoRef={resolveGeoRef}
          />
        </Box>

        <SourcesBlock sources={sources} log={log} workspaceId={workspaceId} campaignSingular={campaignSingular} />
        <AnonymousIdBlock anonId={anonId} />
      </Box>

      <RowActions
        showMerge={showMerge}
        mergeHref={mergeHref}
        showResend={showResend}
        canResend={canResend}
        onResend={openResend}
        showCopyJson={showCopyJson}
        onCopyJson={copyJson}
      />
    </InlineStack>
  );
}
